import {map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {HttpService} from '../http-service.service';
import {ApiConst} from '../api-const';
import {StorageService} from '../../storage/storage.service';
import {SearchCriteria} from '../../../model/search-criteria';
import {ConsultCriteriaService} from './consult-criteria.service';
import {UpdateSearchFiltersService} from '../../crossComponent/update-search-filters.service';


import {CustomDate} from '../../../model/custom-date';
import {
  filterErlStatusMap,
  filterMailStatusMap,
  filterStatusMap
} from "../../../application/application-container/left-container/search/savedRequest/scheduling/helper/filter-status-helper.enum";

/**
 * Service that handleChangeOfSelectFold search request using criterias
 */
@Injectable()
export class SearchService {

  /**
   * list of criterias used for search
   */
  public exposedCriterias: BehaviorSubject<SearchCriteria[]>;

  /**
   * Copy of the last request processed
   */
  public lastRequest;
  public searchRequired: Subject<any>;

  /**
   * @param {StorageService} storageService - only there to store and put in storage Criterias
   * @param {HttpService} httpService - usefull to perform Http Requests
   * @param {ConsultCriteriaService} consultService - fill the consultService with the search result
   * @param {UpdateSearchFiltersService} updateSearchFiltersService  - here to specify if the app should display search filters inside fold details
   */
  constructor(private storageService: StorageService, private httpService: HttpService, private consultService: ConsultCriteriaService,
              private updateSearchFiltersService: UpdateSearchFiltersService) {
    this.exposedCriterias = new BehaviorSubject<SearchCriteria[]>([]);
    this.searchRequired = new Subject<any>();
    this.init();
  }

  /**
   * Handle retrieving info from storage if it's not empty (usefull if Refresh...)
   */
  private init(): void {

    const criteriasFromStorage = this.storageService.getSearchCriteriasFromStorage();
    if (criteriasFromStorage) {
      this.exposedCriterias.next(criteriasFromStorage);
      this.restrictDynamicFilters();
    }
  }

  /**
   * determine if criterias (with associate filter) are present and warn the dedicated service
   */
  private restrictDynamicFilters(): void {
    this.exposedCriterias.subscribe(searchCriterias => {
      let depositNumberIsPresent = false;
      let sendNumberIsPresent = false;
      for (const criteria of searchCriterias) {
        if (SearchCriteria.criteriaIsSendNumber(criteria)) {
          sendNumberIsPresent = true;
        } else if (SearchCriteria.criteriaIsDepositNumber(criteria)) {
          depositNumberIsPresent = true;
        }
      }
      this.updateSearchFiltersService.depositNumberIsPresent.next(depositNumberIsPresent);
      this.updateSearchFiltersService.sendNumberIsPresent.next(sendNumberIsPresent);
    });

  }

  /**
   * build criterias for the service and store them
   * @param toBuild - list of Criterias gave by the back-office
   */
  public buildCriterias(toBuild: any): void {
    const criterias: SearchCriteria[] = [];

    for (let i = 0; i < toBuild.length; i++) {
      criterias.push(
        new SearchCriteria(
          toBuild[i].bDisplay,
          toBuild[i].bRestrictToSuggestedValues,
          toBuild[i].sCategory,
          toBuild[i].sHelpI18n,
          toBuild[i].sLibI18n,
          toBuild[i].sNameI18n,
          toBuild[i].sNameId,
          toBuild[i].sScope,
          toBuild[i].sStorageName,
          toBuild[i].sStorageType,
          toBuild[i].sTypeData,
          ''
        )
      );
    }
    this.storageService.removeSearchCriteriasFromStorage();
    this.storageService.addSearchCriteriasInStorage(criterias);
    this.exposedCriterias.next(criterias);
    this.restrictDynamicFilters();
  }

  /**
   * build the data part for the HttpRequest
   *
   * @param {number} kindOfDate - use the enum DateFiltered
   * @param {boolean} isDeposit - if it's a deposit
   * @param searchOrderDate
   * @return {Object} - the data object
   */
  private buildRequestWithDateFiltered(kindOfDate: number, isDeposit: boolean, searchOrderDate: boolean): any {
    if (searchOrderDate) {
      return {
        bDepotDay: kindOfDate === DateFiltered.TODAY,
        bDepotMonth: kindOfDate === DateFiltered.CURRENT_MONTH,
        bDepotWeek: kindOfDate === DateFiltered.CURRENT_WEEK,
        bDepotYesterday: kindOfDate === DateFiltered.YESTERDAY,
        bLimitedDeposit: isDeposit,
        bLimitedSent: !isDeposit,
        objCriteria: []
      };
    }
    else {
      return {
        bSentDay: kindOfDate === DateFiltered.TODAY,
        bSentMonth: kindOfDate === DateFiltered.CURRENT_MONTH,
        bSentWeek: kindOfDate === DateFiltered.CURRENT_WEEK,
        bSentYesterday: kindOfDate === DateFiltered.YESTERDAY,
        bLimitedDeposit: false,
        bLimitedSent: true,
        objCriteria: []
      };
    }
  }

  /**
   * build the data part for the HttpRequest
   *
   * @param {number} weekNumber - the number of the week (1-52) that is required
   * @param {boolean} isDeposit - if it's a deposit
   * @param searchOrderDate
   * @return {Object} - the data object
   */
  private buildRequestWithWeekNumber(weekNumber: number, isDeposit: boolean, searchOrderDate: boolean): any {
    const req = {
      bDepotDay: false,
      bDepotMonth: false,
      bDepotWeek: false,
      bDepotYesterday: false,
      bLimitedDeposit: isDeposit,
      bLimitedSent: !isDeposit,
      objCriteria: []
    };
    req[searchOrderDate ? 'iDepotWeekNum' : 'iSentWeekNum'] = weekNumber;
    return req;
  }

  /**
   * build the data part for the HttpRequest
   *
   * @param {number} monthNumber - the number of the month (1-12) that is required
   * @param {boolean} isDeposit - if it's a deposit
   * @param searchOrderDate
   * @return {Object} - the data object
   */
  private buildRequestWithMonthNumber(monthNumber: number, isDeposit: boolean, searchOrderDate: boolean): any {
    const req = {
      bDepotDay: false,
      bDepotMonth: false,
      bDepotWeek: false,
      bDepotYesterday: false,
      bLimitedDeposit: isDeposit,
      bLimitedSent: !isDeposit,
      objCriteria: []
    };
    req[searchOrderDate ? 'iDepotMonthNum' : 'iSentMonthNum'] = monthNumber;
    return req;
  }

  /**
   *
   * @param {Date[]} dates - the period of time asked for the request
   * @param {boolean} isDeposit - if it's a deposit
   * @param searchOrderDate
   * @return {Object} - the data object
   */
  private buildRequestWithCustomPeriod(dates: Date[], isDeposit: boolean, searchOrderDate: boolean): any {

    let dateEnd;

    const dateBegin = CustomDate.getDayStartFromDateForRequest(dates[0]);

    if (dates.length < 2) {
      dateEnd = dateBegin;
    }
    else {
      dateEnd = CustomDate.getDayEndFromDateForRequest(dates[1]);
    }

    const req =  {
      bDepotDay: false,
      bDepotMonth: false,
      bDepotWeek: false,
      bDepotYesterday: false,
      bLimitedDeposit: isDeposit,
      bLimitedSent: !isDeposit,
      objCriteria: []
    };
    req[searchOrderDate ? 'dateDepotBegin' : 'dateSentBegin'] = dateBegin;
    req[searchOrderDate ? 'dateDepotEnd' : 'dateSentEnd'] = dateEnd;
    return req;
  }

  /**
   *
   * @param {string} status - the status to filter with
   * @return an object that represent a criteria

  private buildCriteriaUsingStatus(status: string) {
    return {
      sComparatorTrackingValue: '=',
      sCriteriaTrackingValue: status,
      sNameId: 'rawStatus',
      bDisplay: true,
      bRestrictToSuggestedValues: false,
      sCategory: 'CRITERIA',
      sHelpI18n: '',
      sLibI18n: '',
      sNameI18n: '',
      sScope: 'TRACKING-FOLDS',
      sStorageName: 'CledEnvois.rawStatus',
      sStorageType: 'BDD',
      sTypeData: 'string'
    };
  }*/

  /**
   * Perform (if it exists) the last possible request
   */
  public performLastSearch(): Observable<boolean> {
    return this.search(this.lastRequest.criterias, this.lastRequest.kindOfSearch, this.lastRequest.isDeposit, this.lastRequest.proofType, this.lastRequest.statusType, this.lastRequest.mailType, this.lastRequest.erlType, this.lastRequest.searchOrderDate, this.lastRequest.dateValue);
  }
  public searchRequested() {
    this.searchRequired.next(true);
  }
  /**
   *
   * Request to the back-office using HttpService
   *
   * @param {SearchCriteria[]} criterias - list of filters that are filled
   * @param {number} kindOfSearch - use DateFiltered
   * @param {boolean} isDeposit - if it's a deposit
   * @param proofType
   * @param statusType
   * @param mailType
   * @param erlType
   * @param searchOrderDate
   * @param {any} dateValue - can be a number (for month / week) or a Dates[]
   * @return {Observable<Boolean>} - the result of the search request
   */
  public search(criterias: SearchCriteria[], kindOfSearch: number, isDeposit: boolean, proofType: string, statusType: string, mailType: string, erlType: string, searchOrderDate: boolean, dateValue: any): Observable<boolean> {
    if (!searchOrderDate) {
      isDeposit = false;
    }
    this.lastRequest = {
      criterias: criterias.map(x => Object.assign({}, x)),
      kindOfSearch: kindOfSearch,
      isDeposit: isDeposit,
      proofType: proofType,
      statusType: statusType,
      mailType: mailType,
      erlType: erlType,
      searchOrderDate: searchOrderDate,
      dateValue: dateValue
    };

    const datas = this.buildDatesForRequest(kindOfSearch, isDeposit, dateValue, proofType, statusType, mailType, erlType, searchOrderDate);
    datas.objCriteria = this.buildSearchCriteriasForRequest(criterias);
    const datasToSend = JSON.stringify({
      datas: datas
    });

    return this.httpService.$post(ApiConst.getListOfFolds, datasToSend).pipe(
      map((response) => {
        let result;
        if (response) {
          result = response;
        }
        if (result) {
          this.consultService.fillConsultWithResults(result.body);
          return !!( response.body && response.body.bOK);
        } else {
          return true;
        }
      }));
  }

  /**
   *
   * Request to the back-office using HttpService to get the result of a search as a csv
   *
   * @param {SearchCriteria[]} criterias - list of filters that are filled
   * @param {number} kindOfSearch - use DateFiltered
   * @param {boolean} isDeposit - if it's a deposit
   * @param proofType
   * @param statusType
   * @param mailType
   * @param erlType
   * @param searchOrderDate
   * @param {any} dateValue - can be a number (for month / week) or a Dates[]
   * @return {Observable<Boolean>} - the result of the search request
   */
  public export(criterias: SearchCriteria[], kindOfSearch: number, isDeposit: boolean, proofType: string, statusType: string, mailType: string, erlType: string, searchOrderDate: boolean, dateValue): Observable<any> {
    if (!searchOrderDate) {
      isDeposit = false;
    }
    const datas = this.buildDatesForRequest(kindOfSearch, isDeposit, dateValue, proofType, statusType, mailType, erlType, searchOrderDate);
    datas.objCriteria = this.buildSearchCriteriasForRequest(criterias);
    const datasToSend = JSON.stringify({
      datas: datas
    });
    return this.httpService.$post(ApiConst.exportListOfFolds, datasToSend).pipe(
      map((response) => {
        return response;
      }));
  }

  /**
   * build dates for search
   *
   * @param {number} kindOfSearch - use DateFiltered
   * @param {boolean} isDeposit - if it's a deposit
   * @param {any} dateValue - can be a number (for month / week) or a Dates[]
   * @param proofType
   * @param statusType
   * @param mailType
   * @param erlType
   * @param searchOrderDate
   * @return {any} - the date part of the request reformatted for back-office
   */
  private buildDatesForRequest(kindOfSearch: number, isDeposit: boolean, dateValue: any, proofType: string, statusType: string, mailType: string, erlType: string, searchOrderDate: boolean): any {
    let datas;
    switch (kindOfSearch) {
      case DateFiltered.OTHER_MONTH:
        datas = this.buildRequestWithMonthNumber(dateValue, isDeposit, searchOrderDate);
        break;
      case DateFiltered.OTHER_WEEK:
        datas = this.buildRequestWithWeekNumber(dateValue, isDeposit, searchOrderDate);
        break;
      case DateFiltered.CUSTOM_PERIOD:
        datas = this.buildRequestWithCustomPeriod(dateValue, isDeposit, searchOrderDate);
        break;
      default:
        datas = this.buildRequestWithDateFiltered(kindOfSearch, isDeposit, searchOrderDate);

    }
    switch (proofType) {
      case 'noBack':
        datas.noBACK = 'Y';
        break;
        case 'hasAR':
        datas.hasAR = 'Y';
        break;
      case 'hasPND':
        datas.hasPND = 'Y';
        break;
      case 'hasHISTO':
        datas.hasHISTO = 'Y';
        break;
      case 'hasPLAT':
        datas.hasPLAT = 'Y';
        break;
      case 'hasPLATResponse':
        datas.hasPLATResponse = 'Y';
        break;
      case 'isUnsuccessfulPLAT':
        datas.hasPLATResponse = 'Y';
        datas.isUnsuccessfulPLAT = 'Y';
        break;
      case 'isCertificatePLAT':
        datas.hasPLATResponse = 'Y';
        datas.isCertificatePLAT = 'Y';
        break;
      case 'isClaimedPLAT':
        datas.hasPLATResponse = 'Y';
        datas.isClaimedPLAT = 'Y';
        break;
      case 'isInternationalPLAT':
        datas.hasPLATResponse = 'Y';
        datas.isInternationalPLAT = 'Y';
        break;
    }

    filterStatusMap.forEach(status => {
      if(status === statusType)
        datas[status] = 'Y';
    });

    filterMailStatusMap.forEach(status => {
      if(status === mailType)
        datas[status] = 'Y';
    });

    filterErlStatusMap.forEach(status => {
      if(status === erlType)
        datas[status] = 'Y';
    });

    return datas;
  }

  /**
   * build searchCriterias properly formatted for search request
   * @param {SearchCriteria[]} criterias - list of criterias seized by user
   * @return {Array} - the filled criterias for request
   */
  private buildSearchCriteriasForRequest(criterias: SearchCriteria[]): any[] {

    const objCriterias = [];
    for (const criteria of criterias) {
      if (criteria.sCriteriaTrackingValue !== '' && criteria.sCriteriaTrackingValue !== null) {
        objCriterias.push({
          bDisplay: criteria.bDisplay,
          bRestrictToSuggestedValues: criteria.bRestrictToSuggestedValues,
          sCategory: criteria.sCategory,
          sHelpI18n: criteria.sHelpI18n,
          sLibI18n: criteria.sLibI18n,
          sNameI18n: criteria.sNameI18n,
          sNameId: criteria.sNameId,
          sScope: criteria.sScope,
          sStorageName: criteria.sStorageName,
          sStorageType: criteria.sStorageType,
          sComparatorTrackingValue: criteria.sComparatorTrackingValue,
          sCriteriaTrackingValue: criteria.sCriteriaTrackingValue,
          sTypeData: criteria.sTypeData
        });
      }
    }

    return objCriterias;
  }

}

/**
 * Enum that represents the kind of Request asked during the search
 */
export enum DateFiltered {
  TODAY = 1,
  YESTERDAY = 2,
  CURRENT_WEEK = 3,
  CURRENT_MONTH = 4,
  OTHER_WEEK = 5,
  OTHER_MONTH = 6,
  CUSTOM_PERIOD = 7
}

